import React, { CSSProperties, memo, useEffect, useState } from 'react'
import { Stack } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Map } from 'src/Components/Map'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import { MapCoordinates } from 'src/Components/Map/mapTypeDefs'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Typography as TranslationTypography } from '@e3dc-react/shell/Elements/Typography'
import { ActionPositions, UpToThreeActions } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { fetchChargingLocationQueryKey, getCurrentChargingLocation } from '../../functions'
import { useFetchChargingLocation } from '../../hooks'
import { updateChargingLocation } from '../../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useQueryClient } from '@tanstack/react-query'
import { ChargingLocationModel } from '../../models'
import { farmIconSvgUrl as farmIconSrc } from '@e3dc-react/icons'

const useStyles = makeStyles()(theme => ({
  dialogTitle: {
    /* the dialog title users h2 but we want h3 styles. */
    ...(theme.typography.h3 as CSSProperties),
    marginLeft: theme.spacing(4),
  },
  infoBox: {
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.shades.shade03,
    backgroundColor: theme.palette.shades.shade04,
    padding: theme.spacing(2),
  },

  map: {
    height: '400px',
  },
}))

interface Props {
  isModalOpen: boolean
  onClose: (successful: boolean) => void
}

const DEFAULT_ZOOM = 18

/**
 * This component allows user to confirm / update charging location on map.
 * initial location is either charging location, installation location or default location (E3DC office osnabruck)
 * User can confirm or edit the location by dragging the marker on the map.
 *
 * The component is memoized to optimize re-renders of the map component. (otherwise causes flickering)
 */
const ChargingLocationMapModal: React.FC<Props> = memo(({ isModalOpen, onClose }) => {
  const { openCookieModal } = useModals()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const installation = useTypedSelector(state => state.storage.installation)
  const installationId = installation?.id

  const { chargingLocation } = useFetchChargingLocation(installationId)

  /* location that is stored in either installation or vehicle integration db */
  const currentChargingLocation = getCurrentChargingLocation(chargingLocation, installation)

  /* location that the user currently selected on map (initialized with currentChargingLocation) */
  const [selectedLocation, setSelectedLocation] = useState<ChargingLocationModel>(currentChargingLocation)
  const queryKey = fetchChargingLocationQueryKey(installationId)

  useEffect(() => {
    const hasChargingLocation = chargingLocation?.gpsLatitude && chargingLocation?.gpsLongitude

    if (!hasChargingLocation) return

    setSelectedLocation(chargingLocation)
  }, [chargingLocation])

  /* convert to format used by Map component */
  const mapCoordinates: MapCoordinates = {
    lat: selectedLocation.gpsLatitude,
    lng: selectedLocation.gpsLongitude,
  }

  /* store the co-ordinates in vehicle database */
  const handleOnUpdate = async (): Promise<void> => {
    const { hasErrors } = await updateChargingLocation(selectedLocation, installationId, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('vehicle-integration.charging-location-map-modal.error-updating-charging-location'))
      return
    }

    /* refetch the charging location data */
    queryClient.invalidateQueries({ queryKey })

    enqueueSuccessStack(t('vehicle-integration.charging-location-map-modal.success-updating-charging-location'))
    onClose(true)
  }

  /* set the selected location back to the current charging location in database */
  const handleOnReset = (): void => {
    setSelectedLocation(currentChargingLocation)
  }

  const onMarkerDragEnd = (coords: MapCoordinates): void => {
    const { lat, lng } = coords
    const hasCoordinates = lat && lng

    if (!hasCoordinates) return

    /* backend expects 8 decimal places */
    const newChargingLocation: ChargingLocationModel = {
      gpsLatitude: Number(lat.toFixed(8)),
      gpsLongitude: Number(lng.toFixed(8)),
    }

    setSelectedLocation(newChargingLocation)
  }
  const actions: UpToThreeActions = [
    {
      name: t('common.reset'),
      onClick: handleOnReset,
      style: 'secondary',
      position: ActionPositions.One,
    },
    {
      name: t('common.cancel'),
      onClick: () => onClose(false),
      style: 'secondary',
      position: ActionPositions.Three,
    },
    {
      name: t('common.confirm'),
      onClick: handleOnUpdate,
      style: 'primary',
      position: ActionPositions.Four,
    },
  ]

  return (
    <Dialog
      open={isModalOpen}
      onClose={() => onClose(false)}
      title={t('vehicle-integration.charging-location-map-modal.confirm-location-title')}
      titleClassName={classes.dialogTitle}
      maxWidth="md"
      actions={actions}
      fullScreen={false}
      closeButtonVisible={false}
    >
      <Stack gap={2} justifyContent="space-between">
        <Stack gap={2}>
          <TranslationTypography variant="body2" sx={{ mt: 2 }}>
            {'vehicle-integration.charging-location-map-modal.confirm-charging-location-text'}
          </TranslationTypography>
          <Stack className={classes.infoBox}>
            <TranslationTypography variant="body2">
              {'vehicle-integration.charging-location-map-modal.confirm-charging-location-info'}
            </TranslationTypography>
          </Stack>
        </Stack>
        <Stack className={classes.map}>
          <Map
            key="confirm-chargingLocation"
            coordinates={[mapCoordinates]}
            center={mapCoordinates}
            defaultZoom={DEFAULT_ZOOM}
            openCookieModal={() => openCookieModal({})}
            mapsToken={process.env.REACT_APP_MAPS_TOKEN ?? ''}
            draggableMarker
            onMarkerDragEnd={onMarkerDragEnd}
            icon={farmIconSrc}
            showOverlay={false}
            hasSettings={false}
          />
        </Stack>
      </Stack>
    </Dialog>
  )
})

ChargingLocationMapModal.displayName = 'ChargingLocationMapModal'

export default ChargingLocationMapModal
